import {
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {isArray, isNil, isString, memoize} from 'lodash-es';
import {
  BehaviorSubject,
  concat,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  arr_first,
  arr_map,
  blank,
  data_get,
  data_is_true,
  fallback,
  filled,
  map_to_arr,
  OptionalValue,
  str_snake_case,
  str_split,
  str_trim,
  str_truncate,
} from '../../../../utils/common';
import {DropdownOptionGroupTemplateDirective} from '../../../../directives/component.directive';
import {NgSelectComponent} from '@ng-select/ng-select';
import {ApiService} from '../../../../../services/api.service';

@Component({
  selector: 'fieldmagic-dropdown-input',
  template: `
    <div
      *ngIf="!standalone"
      class="form-group"
    >
      <div class="d-flex d-flex-gap">
        <fieldmagic-text
          *ngIf="displayLabel || (label | filled)"
          purpose="input"
          [content]="label"
          [withRequiredMarker]="withRequiredMarker"
        >
        </fieldmagic-text>

        <fieldmagic-icon
          *ngIf="tooltip | filled"
          icon="info-circle"
          [tooltip]="tooltip"
        >
        </fieldmagic-icon>
      </div>
      <ng-container *ngTemplateOutlet="inputTmp"></ng-container>
    </div>
    <ng-container
      *ngIf="standalone"
      [ngTemplateOutlet]="inputTmp"
    >
    </ng-container>
    <ng-template #inputTmp>
      <ng-select
        #myInput
        placeholder="{{ placeholder | translate }}"
        class="fieldmagic-dropdown"
        [class.fieldmagic-input-has-error]="errors | filled"
        [disabled]="isDisabled$ | async"
        [items]="options$ | async"
        [clearable]="clearable"
        [typeahead]="typeahead$"
        [loading]="searching$ | async"
        [(ngModel)]="value"
        (change)="onChanged($event)"
        (click)="onTouched()"
        [multiple]="multi"
        [groupBy]="makeGroupNameFactory()"
        bindLabel="text"
        [bindValue]="bindValue"
        appendTo="body"
        [isOpen]="isOpen$ | async"
        [groupValue]="makeGroupValueFactory()"
        (blur)="onBlur()"
      >
        <!-- displayed for the selected dropdown option -->
        <ng-template
          ng-label-tmp
          let-item="item"
          let-clear="clear"
        >
          <span class="ng-value-label">{{
            computeDisplay(item) | translate
          }}</span>
          <span
            *ngIf="multi"
            class="ng-value-icon right selected-close"
            (click)="clear(item)"
          >
            x
          </span>
        </ng-template>

        <!-- displayed for the dropdown options -->
        <ng-template
          ng-option-tmp
          let-item="item"
        >
          <fieldmagic-indent
            *ngIf="(item.is_parent | filled) && !item.is_parent"
          >
            <span>{{ computeDisplay(item) | translate }}</span>
          </fieldmagic-indent>

          <span *ngIf="(item.is_parent | blank) || item.is_parent">{{
            computeDisplay(item) | translate
          }}</span>
        </ng-template>

        <ng-template
          ng-optgroup-tmp
          let-item="item"
        >
          <ng-container *ngIf="optionGroupTmp | blank">
            {{ item | data_get : 'text' | translate }}
          </ng-container>

          <ng-container
            *ngIf="optionGroupTmp | filled"
            [ngTemplateOutlet]="optionGroupTmp"
            [ngTemplateOutletContext]="{
              item: item
            }"
          ></ng-container>
        </ng-template>

        <!-- displayed for results found: only when customizable is enabled -->
        <ng-template
          let-searchTerm="searchTerm"
          ng-notfound-tmp
        >
          <span
            *ngIf="(customizable$ | async) && (searchTerm | filled)"
            class="ng-option"
            (click)="onNewCustomizedOption(searchTerm)"
          >
            {{ 'Add new item for "%s"' | translate | sprintf : [searchTerm] }}
          </span>

          <span
            *ngIf="searchTerm | blank"
            class="ng-option disabled"
            [innerText]="'No items found' | translate"
          >
          </span>
        </ng-template>
      </ng-select>
      <fieldmagic-input-errors
        *ngIf="errors | filled"
        [errors]="errors"
      ></fieldmagic-input-errors>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownInputComponent),
      multi: true,
    },
  ],
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownInputComponent<O extends FieldmagicDropdownOption>
  implements ControlValueAccessor, OnDestroy
{
  value?: OptionalValue<FieldmagicDropdownInputValue<O> | FieldmagicDropdownIdentifiableOption> = null;

  /// the current input label
  @Input() label: string;

  @Input()
  set isOpen(value: any) {
    const opened = data_is_true(value);

    this.isOpen$.next(opened);

    if (opened) {
      setTimeout(() => {
        const target = this.$input;

        if (blank(target)) {
          return;
        }

        target.focus();
      });
    }
  }

  @Input()
  set options(value: O[] | Observable<O[]>) {
    if (isArray(value)) {
      this.options$ = of(value);
    } else {
      this.options$ = value;
    }

    if (!isNil(this.remote)) {
      this.options$ = concat(
        this.options$,
        this.typeahead$.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => this.searching$.next(true)),
          switchMap((term) => this.remote(term)),
          map((options) => this._filterOptions(options)),
          finalize(() => this.searching$.next(false)),
        ),
      );
    } else {
      this.options$ = this.options$.pipe(
        map((options) => this._filterOptions(options)),
      );
    }
  }

  /// indicates that the value should be cleared
  @Input() clearable: boolean = true;

  /// the remote search factory that will be called when search
  /// is perform in this input
  @Input() remote: RemoteSearchFactory<O>;

  /// the placeholder for the dropdown
  @Input() placeholder: string = '';

  @Input() displayLabel: boolean = true;

  /**
   * Indicates that the input is standalone this means that the input
   * will not be enclosed to a form group and the label will not be displayed.
   * It will only display the provided `placeholder` value
   */
  @Input() standalone: boolean = false;

  /**
   * Indicates the dropdown will omit multiple values
   * or users can select multiple values
   */
  @Input() multi: boolean = false;

  @Input() filter: FieldmagicDropdownOptionFilter<O>;

  @Input() grouped: boolean = false;

  @Input() bindValue?: string;

  @Input() withRequiredMarker: boolean = false;

  @Input() errors: string[] | string = [];

  @Input() tooltip?: string;

  @Input()
  set disabled(value: boolean) {
    this.isDisabled$.next(value);
  }

  /// an optional argument to be used to use the dynamic options setup from
  /// adminitrations configure dropdown
  /// Example value assets:status
  @Input()
  set using(value: OptionalValue<string>) {
    if (blank(value)) {
      this.options = [];
      return;
    }

    const [moduleName, fieldName] = str_split(value, {
      delimiter: ':',
    });

    this.options$ = this._api
      .callPrivate$({
        path: '/dropdown/field_options',
        body: {
          field: fieldName,
          module: moduleName,
        },
        onSuccessResponse: (response) => map_to_arr(response),
      })
      .pipe(
        map((options) =>
          arr_map(options, {
            transformer: (option) => ({
              id: data_get(option, 'key'),
              text: data_get(option, 'name'),
              color: data_get(option, 'color'),
            }),
          }),
        ),
      ) as Observable<FieldmagicConfiguredIdentifiableDropdownOption[]>;

    this._using = value;
  }

  @Input()
  set customizable(value: boolean) {
    this.customizable$.next(value);
  }

  @Output('change')
  $onChanged = new EventEmitter<
    FieldmagicDropdownInputValue<O> | FieldmagicDropdownInputValue<O>[]
  >();

  @Output('blur')
  $onBlur = new EventEmitter<void>();

  @ContentChild(DropdownOptionGroupTemplateDirective, {
    read: TemplateRef,
  })
  optionGroupTmp?: DropdownOptionGroupTemplateDirective;

  /// the options resolver
  options$: Observable<any[]>;

  readonly typeahead$ = new Subject<string>();
  readonly isOpen$ = new Subject<boolean>();
  readonly searching$ = new BehaviorSubject<boolean>(false);
  readonly isDisabled$ = new BehaviorSubject<boolean>(false);
  readonly customizable$ = new BehaviorSubject<boolean>(false);

  @ViewChild('myInput')
  private $input: NgSelectComponent;

  private _termSubscription: Subscription;

  private _onChangeFn: FieldmagicDropdownInputOnChangeHandler<O>;
  private _onTouchFn: FieldmagicDropdownInputOnTouchHandler;

  private _using?: OptionalValue<string>;

  constructor(private readonly _api: ApiService) {}

  /// @see OnDestroy::ngOnDestroy
  ngOnDestroy(): void {
    if (this._termSubscription) {
      this._termSubscription.unsubscribe();
    }
  }

  onChanged(value: any | any[]) {
    this.$onChanged.emit(value);

    if (blank(this._onChangeFn)) {
      return;
    }

    this._onChangeFn(value);
  }

  onBlur = () => this.$onBlur.emit();

  onTouched() {
    if (blank(this._onTouchFn)) {
      return;
    }
    this._onTouchFn();
  }

  public clearDropdownSelection(): void {
    if (this.$input) {
        this.$input.clearModel();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled$.next(isDisabled);
  }

  writeValue(value: OptionalValue<any>): void {
    this.value = value;
  }

  registerOnChange(fn: FieldmagicDropdownInputOnChangeHandler<O>): void {
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: FieldmagicDropdownInputOnTouchHandler): void {
    this._onTouchFn = fn;
  }

  computeDisplay(item: O): string {
    if (isString(item)) {
      return item;
    }

    return (item as CustomizableDropdownOption).text;
  }

  makeGroupNameFactory(): (option: O) => string | undefined {
    if (!this.grouped) {
      return undefined;
    }

    const fn = memoize((option: O): string => {
      if (isString(option)) {
        return 'ungrouped';
      }

      return fallback(data_get(option as object, 'groupName'), {
        fallback: () => 'ungrouped',
      });
    });

    return fn.bind(this);
  }

  makeGroupValueFactory(): OptionalValue<
    (text: string, children: O[]) => FieldmagicDropdownOptionGroup<O>
  > {
    if (!this.grouped) {
      return undefined;
    }

    return memoize((text: string, children: O[]) => ({
      text,
      first: arr_first(children),
    })).bind(this);
  }

  onNewCustomizedOption(term: OptionalValue<string>): void {
    if (blank(term)) {
      return;
    }

    term = str_truncate(str_trim(term), {
      max_length: 64,
    });

    const option: FieldmagicConfiguredIdentifiableDropdownOption = {
      id: str_snake_case(term, {
        lowercase: true,
      }),
      text: term,
      color: '#868e96',
    };

    this.writeValue(option);
    this.onChanged(option);

    if (blank(this._using)) {
      return;
    }

    const [module, field] = str_split(this._using, {
      delimiter: ':',
    });

    const options$ = this.options$;

    options$.pipe(
      take(1),
      switchMap((options) => this._api.callPrivate$({
        path: '/dropdown/save',
        body: {
          module: module,
          field: field,
          options: [{
            key: option.id,
            name: option.text,
            position: options.length - 1,
            color: option.color,
          }],
        },
        onSuccessResponse: (_) => true,
      }))
    ).subscribe();

    this.options$ = concat(
      this.options$,
      of([option]),
    );
  }

  private _filterOptions(options: any[]): O[] {
    if (isNil(this.filter)) {
      return options;
    }

    return options.filter((option) => this.filter(option));
  }
}

type CustomizableDropdownOption = {
  /// the displayed information
  text: string;
  /// the group name for this option. This works when input is flagged as grouped
  groupName?: string;
  /// an indicator used that describes if the option is considered a parent or child
  /// this option can be selected even if its flagged as a parent
  /// this works like a grouped option but the group option is selectable
  is_parent?: boolean;
};

export type FieldmagicDropdownOption<T = Record<string, any>> =
  T extends Record<string, any> ? CustomizableDropdownOption & T : string;
export type FieldmagicDropdownInputValue<O extends FieldmagicDropdownOption> =
  | O
  | null
  | undefined;
export type FieldmagicDropdownInputOnChangeHandler<
  O extends FieldmagicDropdownOption,
> = (
  value: FieldmagicDropdownInputValue<O> | FieldmagicDropdownInputValue<O>[],
) => void;
export type FieldmagicDropdownInputOnTouchHandler = () => void;
export type RemoteSearchFactory<O extends FieldmagicDropdownOption> = (
  term: string,
) => Observable<O[]>;
export type DropdownOption = FieldmagicDropdownOption;
export type FieldmagicDropdownOptionFilter<O extends FieldmagicDropdownOption> =
  (option: O) => boolean;
export type FieldmagicDropdownOptionGroup<O extends FieldmagicDropdownOption> =
  {
    text: string;
    first: O;
  };

export type FieldmagicDropdownIdentifiableOption<T = Record<string, any>> =
  FieldmagicDropdownOption<
    T & {
      id: string;
    }
  >;

export type FieldmagicConfiguredIdentifiableDropdownOption =
  FieldmagicDropdownIdentifiableOption<{
    color: string;
  }>;
